import { Account } from './account';
import { FormContainer } from '../fxfs/schema/form.schema';
import { JWK } from 'jose';
import { Bank } from '../services/interfaces/bank.interface';

export interface Session {
	sessionId: string;
	token: string;
	vendor: string;
	bank?: SessionBank;
}

export interface SessionBank {
	id?: number;
	bankName: string;
	bankCode: string;
	countryCode: string;
	logo?: string;
}

export type SessionState = { bank?: Bank } & (
	| ProcessingState
	| ConsumerInputNeededState
	| FinishedState
	| AbortedState
	| ExceptionState
	| AuthRejectedState
	| ConsentExpiredState
);

export interface ProcessingState {
	status: 'PROCESSING';
	polling?: boolean;
}

export interface FinishedState {
	status: 'FINISHED';
}

export interface AbortedState {
	status: 'ABORTED';
}

export interface AuthRejectedState {
	status: 'AUTH_REJECTED';
}

export interface ConsentExpiredState {
	status: 'CONSENT_EXPIRED';
}

export interface ExceptionState {
	status: 'EXCEPTION';
	result: {
		type: 'error';
		error: {
			category: string;
			message: string;
		};
	};
}

export type ConsumerInputNeededState = { polling?: boolean } & (
	| SessionChooseAccountState
	| SessionRedirectState
	| SessionChooseBankState
	| SessionFormState
);

export interface SessionChooseBankState {
	status: 'CONSUMER_INPUT_NEEDED';
	result: {
		type: 'bank_selection';
	};
}

export interface SessionRedirectState {
	status: 'CONSUMER_INPUT_NEEDED';
	result: RedirectAction;
}

export interface RedirectAction {
	type: 'redirect';
	url: string;
}

export interface SessionChooseAccountState {
	status: 'CONSUMER_INPUT_NEEDED';
	result: ChooseAccountAction;
}

export interface ChooseAccountAction {
	type: 'account_selection';
	accounts: Array<Account>;
}

export interface SessionFormState {
	status: 'CONSUMER_INPUT_NEEDED';
	result: FormAction;
}

export interface FormAction {
	type: 'form';
	form: FormContainer;
	key: JWK;
	context?: FormContext;
}

export type LegacyFormContext = 'authentication' | 'transport_selection';
export type FormContext = 'auth:credentials' | '2FA:selection' | '2FA:input' | '2FA:pending' | LegacyFormContext;

// helpers

export const isFinishedSessionState = (state: SessionState): state is FinishedState => {
	return state.status === 'FINISHED';
};

export const isSessionConsumerInputNeededState = (state: SessionState): state is ConsumerInputNeededState => {
	return state.status === 'CONSUMER_INPUT_NEEDED';
};

export const isProcessingSessionState = (state: SessionState): state is ProcessingState => {
	return state.status === 'PROCESSING';
};

export const isAbortedSessionState = (state: SessionState): state is AbortedState => {
	return state.status === 'ABORTED';
};

export const isExceptionSessionState = (state: SessionState): state is ExceptionState => {
	return state.status === 'EXCEPTION';
};

export const isChooseAccountSessionState = (state: SessionState): state is SessionChooseAccountState => {
	return isSessionConsumerInputNeededState(state) && state.result.type === 'account_selection';
};

export const isChooseBankSessionState = (state: SessionState): state is SessionChooseBankState => {
	return isSessionConsumerInputNeededState(state) && state.result.type === 'bank_selection';
};

export const isRedirectSessionState = (state: SessionState): state is SessionRedirectState => {
	return isSessionConsumerInputNeededState(state) && state.result.type === 'redirect';
};

export const isFormSessionState = (state: SessionState): state is SessionFormState => {
	return isSessionConsumerInputNeededState(state) && state.result.type === 'form';
};
